'use client'

import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useRouter } from 'next/navigation'
import { formValidation } from '@/lib/formValidation'
import { RecaptchaModal } from '@/lib/recaptcha/components'

interface HomeTopFormProps {
	data?: any
}

type FormData = {
	firstName: string
	lastName: string
	phone: string
	email: string
	message: string
	city: string // Honeypot
	marketing: boolean
	recaptchaToken: string
}

export default function HomeTopForm({ data }: HomeTopFormProps) {
	const [isSubmitting, setIsSubmitting] = useState(false)
	const [formError, setFormError] = useState<string>('')
	const [formSuccess, setFormSuccess] = useState<string>('')
	const [isFormVisible, setIsFormVisible] = useState(false)
	const [showRecaptcha, setShowRecaptcha] = useState(false)
	const [recaptchaToken, setRecaptchaToken] = useState<string>('')
	const [pendingFormData, setPendingFormData] = useState<FormData | null>(null)
	const router = useRouter()

	const {
		register,
		handleSubmit,
		formState: { errors },
		setValue,
	} = useForm<FormData>({
		mode: 'onBlur',
	})

	const handleRecaptchaVerify = (token: string) => {
		setRecaptchaToken(token)
		setValue('recaptchaToken', token)
		setFormError('')
		setShowRecaptcha(false)
		
		// Submit form after recaptcha verification
		if (pendingFormData) {
			submitFormWithToken(pendingFormData, token)
		}
	}

	const handleRecaptchaError = (error: string) => {
		setFormError(error)
	}

	const submitFormWithToken = async (data: FormData, token: string) => {
		setIsSubmitting(true)
		setFormError('')
		setFormSuccess('')

		try {
			const response = await fetch('/api/contact-form/', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify({ ...data, recaptchaToken: token }),
			})

			if (!response.ok) {
				const errorData = await response.json()
				throw new Error(errorData.error || 'Failed to submit')
			}
			
			setIsFormVisible(false)
			// Redirect to thank you page immediately after successful submission
			router.push('/thank-you')
		} catch (error: any) {
			setFormError(error.message || 'Failed to submit. Please try again.')
			// Reset recaptcha token on error
			setRecaptchaToken('')
			setPendingFormData(null)
		} finally {
			setIsSubmitting(false)
		}
	}

	const onSubmit = async (data: FormData) => {
		// Show recaptcha modal before submitting
		setPendingFormData(data)
		setShowRecaptcha(true)
	}

	return (
		<>
			<section id='cftophome' className='bg-gray-slate h-auto w-full'>
				<div className='container'>
					<div className='mx-auto py-6 md:py-8'>
						<div className='flex flex-col justify-between h-full'>
							<div className='flex lg:gap-8 w-full max-lg:flex-wrap max-lg:justify-center'>
								{/* Desktop heading */}
								<div className='flex whitespace-nowrap items-center gap-2'>
									<h2
										onClick={() => setIsFormVisible(!isFormVisible)}
										className='max-lg:text-4xl text-neutral-50 text-lg lg:uppercase leading-9 tracking-wide cursor-pointer'
									>
										Contact Us
									</h2>
								</div>

								{/* Form container with smooth transition for mobile */}
								<div
									className={`w-full transition-all duration-300 ease-in-out ${
										isFormVisible
											? 'max-h-[1000px] opacity-100'
											: 'max-lg:max-h-0 max-lg:opacity-0 max-lg:overflow-hidden lg:block'
									}`}
								>
									<form
										onSubmit={handleSubmit(onSubmit)}
										className='w-full relative pb-[1.5625rem] max-lg:pt-7'
									>
										<div className='flex flex-col lg:flex-row gap-4'>
											<input type='hidden' {...register('city')} />
											<input type='hidden' {...register('recaptchaToken')} />

											{/* Input Fields */}
											<input
												{...register('firstName', { required: true })}
												type='text'
												placeholder='First Name *'
												className='w-full lg:w-[12%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50'
												aria-required='true'
											/>

											<input
												{...register('lastName', { required: true })}
												type='text'
												placeholder='Last Name *'
												className='w-full lg:w-[12%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50'
												aria-required='true'
											/>

											<input
												{...register('phone', {
													required: true,
													pattern:
														/^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{4,6}$/,
												})}
												type='tel'
												placeholder='Phone Number *'
												className='w-full lg:w-[12%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50'
												aria-required='true'
											/>

											<input
												{...register('email', {
													required: true,
													pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
												})}
												type='email'
												placeholder='E-mail Address *'
												className='w-full lg:w-[12%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50'
												aria-required='true'
											/>

											<input
												{...register('message', { required: true })}
												type='text'
												placeholder='Message *'
												className='w-full lg:w-[40%] bg-transparent text-neutral-50 text-sm font-brandon leading-normal max-lg:leading-[2.5] placeholder-neutral-50 focus:outline-none border-b border-neutral-50'
												aria-required='true'
											/>

											{/* Checkbox */}
											<div className='flex justify-between items-center mt-4 lg:absolute lg:bottom-0 lg:right-0'>
												<div className='flex-1' />
												<div className='flex flex-col gap-1 lg:mr-[6.8rem]'>
													<label className='flex items-center gap-2'>
														<div className='relative w-3 h-3 border border-neutral-50'>
															<input
																type='checkbox'
																{...register('marketing', { required: true })}
																className='absolute w-full h-full cursor-pointer accent-neutral-50'
																aria-required='true'
																aria-invalid={errors.marketing ? 'true' : 'false'}
															/>
														</div>
														<span className='text-neutral-50 text-xs font-brandon leading-tight'>
															I consent to receive phone calls, text messages,
															and emails from Harmon Facial Plastic Surgery.
														</span>
													</label>
													{errors.marketing && (
														<span className='text-red-400 text-xs font-brandon'>
															Please check the consent box to submit the form.
														</span>
													)}
												</div>
											</div>

											<button
												type='submit'
												disabled={isSubmitting}
												className='relative lg:top-[0.625rem] lg:ml-4 px-6 py-2.5 bg-transparent border border-neutral-50 rounded text-neutral-50 text-base font-brandon leading-7 tracking-[0.5px] hover:bg-neutral-50/10 transition-colors disabled:opacity-50 whitespace-nowrap'
											>
												Send
											</button>
										</div>
									</form>
								</div>
							</div>
						</div>
					</div>

					{formError && (
						<div className='text-red-400 text-sm text-center'>{formError}</div>
					)}

					{formSuccess && (
						<div className='text-green-400 text-sm text-center'>
							{formSuccess}
						</div>
					)}
				</div>
			</section>

			{/* reCAPTCHA Modal */}
			<RecaptchaModal
				isOpen={showRecaptcha}
				onClose={() => {
					setShowRecaptcha(false)
					setPendingFormData(null)
				}}
				onVerify={handleRecaptchaVerify}
				onError={handleRecaptchaError}
				title='Verify you are human'
			/>
		</>
	)
}
